/**
 * V1WsClient — Bowtie v1 WebSocket client.
 *
 * Same public surface as VisionWSClient (same `onIntervention`,
 * `onStatus`, `sendActionComplete`, `sendRRWebEvent`, `close`, `connect`
 * methods + callbacks) so ActionExecutor and widget.setObserver can
 * treat v1 and legacy transports identically.
 *
 * Protocol (see server/src/bowtie/widget/widgetSession.ts):
 *   Client → Server:
 *     hello, signal, page, identify, track, user_reply, action_ack, tick
 *   Server → Client:
 *     welcome, action, orch_tick, error
 *
 * Dwell + scroll signals are emitted on a 5s heartbeat while connected
 * so the server's orchestrator has live trigger fuel without the widget
 * having to call the SDK for every tick.
 */

import type { eventWithTime } from '@rrweb/types'

/**
 * Re-export the intervention/status surface ActionExecutor consumes.
 * These were the legacy /vision protocol shapes; we keep the SHAPE so
 * ActionExecutor + the chat-pill render path stay unchanged, and
 * V1WsClient translates inbound v1 actions to these on the fly.
 */
export interface InterventionMessage {
  type: 'suggest_intervention'
  actionId: string
  message: string
  goalId: string
  context: string
}

export interface StatusMessage {
  type: 'status'
  state: 'observing' | 'paused' | 'acting'
}

export type ActionResult = 'accepted' | 'declined' | 'ignored'

interface V1InitPayload {
  staffId: string
  visitorId: string
  sessionId?: string
  tenantId?: string
  pageUrl: string
  pageTitle: string
  pagePatterns?: Array<{ pattern: string; container?: string }>
}

interface SignalObs {
  path: string
  value: unknown
  source: 'widget' | 'sdk'
  confidence: number
  at: string
}

interface ActionMessage {
  type: 'action'
  actionId: string
  category: 'converse' | 'browser_act' | 'handoff' | 'dispatch'
  skillSlug: string
  skillVersion?: string
  payload: Record<string, unknown>
  orchestratorTraceId?: string | null
  createdAt?: string
}

interface OrchTickMessage {
  type: 'orch_tick'
  outcome: string
  chosen: string | null
  reasoning: string
  traceId?: string
}

interface WelcomeMessage {
  type: 'welcome'
  sessionId: string
  resolvedLocale: string | null
}

interface ErrorMessage {
  type: 'error'
  message: string
}

type InboundMessage = WelcomeMessage | ActionMessage | OrchTickMessage | ErrorMessage

const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30_000
const HEARTBEAT_MS = 5000

function resolveWsUrl(url: string): string {
  if (url.startsWith('ws://') || url.startsWith('wss://')) return url
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${location.host}${url.startsWith('/') ? '' : '/'}${url}`
}

const PAGE_TYPE_RULES: Array<[RegExp, string]> = [
  [/\b(pricing|plans|price)\b/, 'pricing'],
  [/\b(docs?|help|support)\b/, 'docs'],
  [/\b(blog|post|article)\b/, 'blog'],
  [/\b(case[-_]study|customers?)\b/, 'case_study'],
  [/\b(sign[-_]?up|register|join)\b/, 'signup'],
  [/\b(checkout|cart|pay)\b/, 'checkout'],
  [/\b(settings|account|preferences)\b/, 'app_settings'],
  [/\b(billing|subscription)\b/, 'app_billing'],
  [/\b(dashboard|overview)\b/, 'app_home'],
]

function inferPageTypeForPath(path: string): string {
  const p = path.toLowerCase()
  if (p === '/' || p === '') return 'landing'
  for (const [regex, value] of PAGE_TYPE_RULES) {
    if (regex.test(p)) return value
  }
  return 'unknown'
}
function pageAuthedForPath(path: string): boolean {
  return /^\/(app|dashboard|account|admin)\b/.test(path.toLowerCase())
}

function inferPageType(): string { return inferPageTypeForPath(location.pathname) }
function pageAuthed(): boolean { return pageAuthedForPath(location.pathname) }

export class V1WsClient {
  // Public callbacks — same shape as VisionWSClient
  onIntervention: ((msg: InterventionMessage) => void) | null = null
  onStatus: ((msg: StatusMessage) => void) | null = null

  private url: string
  private init: V1InitPayload
  private sessionId: string
  private ws: WebSocket | null = null
  private _closed = false
  private reconnectDelay = RECONNECT_BASE_MS
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private pendingOutbound: object[] = []
  private welcomed = false

  // Heartbeat state
  private startedAt = Date.now()
  private maxScrollPct = 0
  private tabBlurred = false
  private tabReturnCount = 0
  private conversationActive = false
  private _onScroll = this.handleScroll.bind(this)
  private _onVisibility = this.handleVisibility.bind(this)

  constructor(url: string, init: V1InitPayload) {
    this.url = resolveWsUrl(url)
    this.init = init
    this.sessionId = init.sessionId
      ?? `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
  }

  // ────── Public API (mirrors VisionWSClient) ──────

  connect(): void {
    if (this._closed) return
    this.open()
    window.addEventListener('scroll', this._onScroll, { passive: true })
    document.addEventListener('visibilitychange', this._onVisibility)
  }

  close(): void {
    this._closed = true
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null }
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null }
    window.removeEventListener('scroll', this._onScroll)
    document.removeEventListener('visibilitychange', this._onVisibility)
    if (this.ws) {
      try { this.ws.close() } catch { /* noop */ }
      this.ws = null
    }
  }

  /**
   * Stream an rrweb event over the v1 WS. The server-side per-session
   * vision pipeline (server/src/bowtie/vision/sessionPipeline.ts)
   * buffers these, replays into Browserbase, runs the VLM, and writes
   * derived signals + salience notes into bowtie memory.
   *
   * Events are sent best-effort: dropped silently if the WS isn't yet
   * welcomed (the boot signal-burst takes priority over backfill).
   */
  sendRRWebEvent(event: eventWithTime): void {
    if (!this.welcomed) return
    this.sendNow({ type: 'rrweb', event })
  }

  /**
   * Translate legacy ActionResult ('accepted' / 'declined' / 'ignored')
   * to v1 action_ack + a track event so observability captures the
   * accept/decline signal.
   */
  sendActionComplete(actionId: string, result: ActionResult): void {
    this.send({
      type: 'action_ack',
      actionId,
      status: result === 'accepted' ? 'delivered' : 'failed',
      error: result !== 'accepted' ? result : undefined,
    })
    if (result === 'accepted') {
      this.conversationActive = true
      this.send({ type: 'track', event: 'intervention_accepted', properties: { actionId } })
    } else {
      this.send({ type: 'track', event: 'intervention_' + result, properties: { actionId } })
    }
  }

  /** Let the widget report when its realtime session starts / ends. */
  sendConversationStarted(): void {
    this.conversationActive = true
    this.send({ type: 'track', event: 'conversation_started' })
  }
  sendConversationEnded(summary?: string): void {
    this.conversationActive = false
    this.send({
      type: 'track',
      event: 'conversation_ended',
      properties: summary ? { summary } : undefined,
    })
  }

  /**
   * Legacy VisionWSClient notifies server on SPA path change; v1 forwards
   * the same info as a `page` event so the server can re-infer stage +
   * call recordStageChange to refill the suppression budget.
   *
   * Signature matches VisionWSClient.sendPageChanged so `visionWs` can
   * be called uniformly from the SPA router in index.ts.
   */
  sendPageChanged(url: string, pathname: string, title: string): void {
    this.send({ type: 'page', url, path: pathname, title })
    // Also emit a fresh page.type signal so stage inference reacts on
    // the next orchestration tick. We use the same heuristic as the
    // boot burst.
    const at = new Date().toISOString()
    this.send({
      type: 'signal',
      signals: [
        { path: 'page.type',   value: inferPageTypeForPath(pathname), source: 'widget', confidence: 0.7, at },
        { path: 'page.authed', value: pageAuthedForPath(pathname),    source: 'widget', confidence: 0.6, at },
      ] as SignalObs[],
    })
    // Dwell restarts per visited page — heartbeat state reset.
    this.startedAt = Date.now()
    this.maxScrollPct = 0
  }

  /** Explicit "quiet for this visit" gesture — long-press on the pill. */
  sendSilenceGesture(): void {
    this.send({ type: 'track', event: 'intervention_silenced', properties: {} })
  }

  // ────── Internal ──────

  private open(): void {
    try {
      this.ws = new WebSocket(this.url)
    } catch (err) {
      console.warn('[V1WsClient] open threw:', (err as Error).message)
      this.scheduleReconnect()
      return
    }

    this.ws.onopen = () => {
      this.reconnectDelay = RECONNECT_BASE_MS
      this.sendNow({
        type: 'hello',
        staffId: this.init.staffId,
        visitorId: this.init.visitorId,
        sessionId: this.sessionId,
        tenantId: this.init.tenantId ?? this.init.staffId,
        startedAt: new Date().toISOString(),
        page: {
          url: this.init.pageUrl,
          path: new URL(this.init.pageUrl, 'http://x').pathname,
          title: this.init.pageTitle,
          referrer: document.referrer || undefined,
        },
        context: {
          locale: navigator.language,
          timezone: this.tz(),
          viewport: { width: window.innerWidth, height: window.innerHeight },
          userAgent: navigator.userAgent,
        },
      })
    }

    this.ws.onmessage = (ev) => this.handleInbound(ev)

    this.ws.onclose = () => {
      this.welcomed = false
      if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null }
      if (!this._closed) this.scheduleReconnect()
    }

    this.ws.onerror = () => { /* onclose handles reconnect */ }
  }

  private handleInbound(ev: MessageEvent): void {
    let msg: InboundMessage
    try { msg = JSON.parse(ev.data) as InboundMessage } catch { return }

    switch (msg.type) {
      case 'welcome':
        this.welcomed = true
        this.sessionId = msg.sessionId
        for (const q of this.pendingOutbound) this.sendNow(q)
        this.pendingOutbound = []
        this.startHeartbeat()
        this.emitBootSignals()
        this.onStatus?.({ type: 'status', state: 'observing' })
        break

      case 'action':
        if (msg.category === 'converse' && typeof msg.payload?.text === 'string') {
          // Translate v1 action → legacy intervention shape so ActionExecutor
          // + the widget's intervention chrome can render it unchanged.
          this.onIntervention?.({
            type: 'suggest_intervention',
            actionId: msg.actionId,
            message: msg.payload.text as string,
            goalId: msg.skillSlug,
            context: `Skill: ${msg.skillSlug}${msg.skillVersion ? ` @${msg.skillVersion}` : ''}`,
          })
        } else {
          // browser_act / handoff / dispatch — v1 UI glue for these lands
          // with the invocation LLM milestone. ACK as delivered for now so
          // the action record reflects receipt.
          this.send({ type: 'action_ack', actionId: msg.actionId, status: 'delivered' })
        }
        break

      case 'orch_tick':
        // Observational — useful for dev overlays. No widget-side action.
        break

      case 'error':
        console.warn('[V1WsClient] server error:', msg.message)
        break
    }
  }

  private scheduleReconnect(): void {
    if (this._closed || this.reconnectTimer) return
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.open()
    }, this.reconnectDelay)
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_MS)
  }

  private send(msg: object): void {
    if (this.welcomed && this.ws?.readyState === WebSocket.OPEN) {
      this.sendNow(msg)
    } else {
      this.pendingOutbound.push(msg)
    }
  }

  private sendNow(msg: object): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    try { this.ws.send(JSON.stringify(msg)) } catch { /* swallow */ }
  }

  private tz(): string | undefined {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone }
    catch { return undefined }
  }

  // ────── Signal emission ──────

  private emitBootSignals(): void {
    const at = new Date().toISOString()
    const visitsKey = `hs_visits_${this.init.staffId}`
    const prior = parseInt(localStorage.getItem(visitsKey) ?? '0', 10)
    const sessions = prior + 1
    try { localStorage.setItem(visitsKey, String(sessions)) } catch { /* storage full etc. */ }

    this.send({
      type: 'signal',
      signals: [
        { path: 'page.type',             value: inferPageType(),   source: 'widget', confidence: 0.7, at },
        { path: 'page.authed',           value: pageAuthed(),      source: 'widget', confidence: 0.6, at },
        { path: 'visitor.returning',     value: prior > 0,         source: 'widget', confidence: 0.95, at },
        { path: 'visitor.session_count', value: sessions,          source: 'widget', confidence: 0.95, at },
      ] as SignalObs[],
    })
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = setInterval(() => this.heartbeat(), HEARTBEAT_MS)
  }

  private heartbeat(): void {
    const at = new Date().toISOString()
    const dwell = Math.floor((Date.now() - this.startedAt) / 1000)
    this.send({
      type: 'signal',
      signals: [
        { path: 'dwell.seconds',                value: dwell,                  source: 'widget', confidence: 1, at },
        { path: 'scroll.depth_pct',             value: this.maxScrollPct,      source: 'widget', confidence: 1, at },
        { path: 'session.conversation_active',  value: this.conversationActive, source: 'widget', confidence: 1, at },
        { path: 'interaction.tab_blurred',      value: this.tabBlurred,        source: 'widget', confidence: 0.9, at },
        { path: 'interaction.tab_return_count', value: this.tabReturnCount,    source: 'widget', confidence: 0.9, at },
      ] as SignalObs[],
    })
  }

  private handleScroll(): void {
    const max = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1)
    const pct = Math.min(100, Math.round((window.scrollY / max) * 100))
    if (pct > this.maxScrollPct) this.maxScrollPct = pct
  }

  private handleVisibility(): void {
    if (document.visibilityState === 'hidden') {
      this.tabBlurred = true
    } else if (this.tabBlurred) {
      this.tabBlurred = false
      this.tabReturnCount += 1
    }
  }
}
